if (!customElements.get('theme-modal')) {
  class ThemeModal extends HTMLElement {
    constructor() {
      super();
      this.detailsContainer = null;
      this.summaryToggle = null;
      this.overlay = null;
      this.previousActiveElement = null;
      this.focusableElements = null;
      this.isAnimating = false;
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this.handleKeyboardShortcut = this.handleKeyboardShortcut.bind(this);
    }

    connectedCallback() {
      this.detailsContainer = this.querySelector('details');
      this.summaryToggle = this.querySelector('summary');
      this.overlay = this.querySelector('[data-modal-overlay]');

      this.summaryToggle?.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.isAnimating) return;
        this.isOpen() ? this.close() : this.open();
      });

      this.querySelectorAll('[data-modal-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      if (this.keyboardShortcut) {
        document.addEventListener('keydown', this.handleKeyboardShortcut);
      }

      if (this.openOnLoad) {
        requestAnimationFrame(() => {
          if (this.isConnected && !this.isOpen()) this.open();
        });
      }
    }

    disconnectedCallback() {
      this._removeListeners();
      if (this.keyboardShortcut) {
        document.removeEventListener('keydown', this.handleKeyboardShortcut);
      }
    }

    get trapFocus() {
      return this.dataset.trapFocus !== 'false';
    }

    get closeOnOverlayClick() {
      return this.dataset.closeOnOverlayClick !== 'false';
    }

    get keyboardShortcut() {
      return this.dataset.keyboardShortcut === 'true';
    }

    get openOnLoad() {
      return this.dataset.openOnLoad === 'true';
    }

    get animationDuration() {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--duration-medium')
        .trim();
      const value = parseFloat(raw) || 0;
      return raw.endsWith('ms') ? value : value * 1000;
    }

    isOpen() {
      return this.detailsContainer?.hasAttribute('open');
    }

    open() {
      if (!this.detailsContainer || this.isAnimating) return;

      this.isAnimating = true;
      const active = document.activeElement;
      this.previousActiveElement = (active && active !== document.body) ? active : this.summaryToggle;
      this.detailsContainer.setAttribute('open', '');
      this.setAttribute('data-open', '');
      this._updateFocusableElements();
      this._addListeners();

      requestAnimationFrame(() => {
        const focusTarget =
          this.querySelector(
            'input[type="search"]:not([disabled]), input[type="text"]:not([disabled]), input:not([type="hidden"]):not([disabled])'
          ) || this.focusableElements?.[0];
        focusTarget?.focus();
      });

      this._publish(theme?.pubsub?.events?.MODAL_OPEN);

      setTimeout(() => {
        this.isAnimating = false;
      }, this.animationDuration);
    }

    close() {
      if (!this.detailsContainer || this.isAnimating) return;

      this.isAnimating = true;
      this.detailsContainer.classList.add('is-closing');
      this._removeListeners();

      setTimeout(() => {
        this.detailsContainer.removeAttribute('open');
        this.detailsContainer.classList.remove('is-closing');
        this.removeAttribute('data-open');
        this.previousActiveElement?.focus();
        this.previousActiveElement = null;
        this.isAnimating = false;

        this._publish(theme?.pubsub?.events?.MODAL_CLOSE);
      }, this.animationDuration);
    }

    toggle() {
      this.isOpen() ? this.close() : this.open();
    }

    _addListeners() {
      document.addEventListener('keydown', this.handleKeyDown);
      if (this.closeOnOverlayClick && this.overlay) {
        this.overlay.addEventListener('click', this.handleOverlayClick);
      }
    }

    _removeListeners() {
      document.removeEventListener('keydown', this.handleKeyDown);
      this.overlay?.removeEventListener('click', this.handleOverlayClick);
    }

    handleKeyDown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }

      if (e.key === 'Tab' && this.trapFocus) {
        this._handleTabKey(e);
      }
    }

    handleOverlayClick() {
      this.close();
    }

    handleKeyboardShortcut(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        this.isOpen() ? this.close() : this.open();
      }
    }

    _handleTabKey(e) {
      this._updateFocusableElements();
      const first = this.focusableElements?.[0];
      const last = this.focusableElements?.[this.focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        last?.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first?.focus();
        e.preventDefault();
      }
    }

    _updateFocusableElements() {
      const selector = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const panel = this.querySelector('[data-modal-panel]') ?? this.detailsContainer ?? this;
      this.focusableElements = [...panel.querySelectorAll(selector)].filter(
        (el) => !el.hidden && !el.disabled && el.offsetParent !== null
      );
    }

    _publish(eventName) {
      if (!eventName || typeof theme === 'undefined' || !theme.pubsub) return;
      theme.pubsub.publish(eventName, { id: this.id });
    }
  }

  customElements.define('theme-modal', ThemeModal);
}

if (!customElements.get('details-modal')) {
  const ThemeModal = customElements.get('theme-modal');
  customElements.define('details-modal', class extends ThemeModal {});
}
